import httpClient from './httpClient';

// Helper function to parse MongoDB ObjectId format
const parseClientData = (client) => {
  if (!client) return client;
  
  return {
    ...client,
    _id: client._id?.$oid || client._id || null,
    created_at: client.created_at?.$date || client.created_at,
    updated_at: client.updated_at?.$date || client.updated_at,
  };
};

const parseClientArray = (clients) => {
  if (!Array.isArray(clients)) return clients;
  
  return clients.map(parseClientData);
};

// Get all clients
const getClients = async (page = 1, pageSize = 50, sortBy = 'name', sortOrder = 'asc', filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters
  };

  const response = await httpClient.get('/api/clients', { params });
  const data = response.data;
  
  // Handle both { clients: [...] } and direct array response
  if (Array.isArray(data)) {
    return {
      clients: parseClientArray(data),
      pagination: { page, page_size: pageSize, total_count: data.length, total_pages: 1 }
    };
  }
  
  if (data.clients) {
    data.clients = parseClientArray(data.clients);
    data.pagination = {
      page: data.page || page,
      page_size: data.page_size || pageSize,
      total_count: data.total_count || 0,
      total_pages: Math.ceil((data.total_count || 0) / pageSize)
    };
  }
  
  return data;
};

// Get single client by ID
const getClient = async (clientId) => {
  const response = await httpClient.get(`/api/clients/${clientId}`);
  const data = response.data;
  
  if (data.client) {
    data.client = parseClientData(data.client);
  }
  
  return data;
};

// Create new client
const createClient = async (clientData) => {
  const response = await httpClient.post('/api/clients', clientData);
  const data = response.data;
  
  if (data.client) {
    data.client = parseClientData(data.client);
  }
  
  return data;
};

// Update client
const updateClient = async (clientId, clientData) => {
  const response = await httpClient.put(`/api/clients/${clientId}`, clientData);
  const data = response.data;
  
  if (data.client) {
    data.client = parseClientData(data.client);
  }
  
  return data;
};

// Delete client
const deleteClient = async (clientId) => {
  const response = await httpClient.delete(`/api/clients/${clientId}`);
  return response.data;
};

// Get client-specific inventory
const getClientInventory = async (clientId, page = 1, pageSize = 20, sortBy = 'name', sortOrder = 'asc', filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters
  };

  const response = await httpClient.get(`/api/clients/${clientId}/inventory`, { params });
  const data = response.data;
  
  if (Array.isArray(data.inventory)) {
    // Already an array (new API shape)
    data.products = parseClientArray(data.inventory);
    data.pagination = {
      page: data.page,
      page_size: data.page_size,
      total_count: data.total_count,
      total_pages: Math.ceil((data.total_count || 0) / (data.page_size || 1))
    };
  } else if (typeof data.inventory === 'string') {
    // Legacy shape — parse JSON string
    try {
      const inv = JSON.parse(data.inventory);
      data.products = parseClientArray(Array.isArray(inv) ? inv : []);
      data.pagination = {
        page: data.page,
        page_size: data.page_size,
        total_count: data.total_count,
        total_pages: Math.ceil((data.total_count || 0) / (data.page_size || 1))
      };
    } catch (e) {
      console.warn('Failed to parse inventory string; returning empty list');
      data.products = [];
      data.pagination = {
        page: data.page,
        page_size: data.page_size,
        total_count: 0,
        total_pages: 0
      };
    }
  }
  
  return data;
};

// Get enhanced client-specific inventory with location, freshness, and PO source info
const getClientInventoryEnhanced = async (clientId, params = {}) => {
  try {
    const queryParams = {
      page: params.page || 1,
      page_size: params.page_size || 1000,
      ...params
    };

    const response = await httpClient.get(`/api/clients/${clientId}/inventory-enhanced`, {
      params: queryParams
    });

    const data = response.data;

    // Group inventory by product for easier consumption
    if (data.inventory && Array.isArray(data.inventory)) {
      const grouped = data.inventory.reduce((acc, item) => {
        const key = item.product_code || item.product_id;
        if (!key) return acc;

        if (!acc[key]) {
          acc[key] = {
            product_code: key,
            product_id: key,
            product_name: item.product_name || item.name,
            specification: item.specification,
            category: item.category,
            unit: item.unit,
            total_quantity: 0,
            locations: [],
            _original: item
          };
        }

        acc[key].locations.push({
          location: item.location,
          available_quantity: item.available_quantity,
          remaining_quantity: item.remaining_quantity,
          inbound_total: item.inbound_total,
          outbound_total: item.outbound_total,
          movement_count: item.movement_count,
          last_movement: item.last_movement,
          source_origins: item.source_origins || [],
          data_freshness: item.data_freshness,
          allocated_quantity: 0 // For user selection
        });

        acc[key].total_quantity += (item.available_quantity || 0);

        return acc;
      }, {});

      // Convert to array and enrich with selection helpers
      data.groupedInventory = Object.values(grouped).map(product => {
        // Determine if this product requires location selection
        const locationsWithStock = product.locations.filter(l => l.available_quantity > 0);
        product.requiresLocationSelection = locationsWithStock.length > 1;

        // Pre-select default location if only one
        if (locationsWithStock.length === 1) {
          product.selectedLocation = locationsWithStock[0].location;
          product.maxAvailable = locationsWithStock[0].available_quantity;
        } else if (locationsWithStock.length > 1) {
          // Smart default: freshest data with most stock
          const freshLocations = locationsWithStock.filter(l => l.data_freshness === 'fresh');
          const sortedByStock = (freshLocations.length > 0 ? freshLocations : locationsWithStock)
            .sort((a, b) => b.available_quantity - a.available_quantity);
          product.selectedLocation = sortedByStock[0]?.location;
          product.maxAvailable = sortedByStock[0]?.available_quantity;
        }

        return product;
      });

      // Keep flat inventory for backward compatibility
      data.products = data.inventory;
    }

    return data;
  } catch (error) {
    console.error('Error fetching enhanced inventory:', error);
    throw error;
  }
};

// Update client inventory for specific product
const updateClientInventory = async (clientId, productId, inventoryData) => {
  const response = await httpClient.put(`/api/clients/${clientId}/inventory/${productId}`, inventoryData);
  const data = response.data;
  
  if (data.product) {
    data.product = parseClientData(data.product);
  }
  
  return data;
};

// Get client-specific products (with overrides applied)
const getClientProducts = async (clientId, page = 1, pageSize = 20, sortBy = 'name', sortOrder = 'asc', filters = {}) => {
  const params = {
    client_id: clientId,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters
  };

  const response = await httpClient.get('/api/products', { params });
  const data = response.data;
  
  if (data.products) {
    data.products = parseClientArray(data.products);
  }
  
  return data;
};

// Get client attribute overrides for a product
const getClientProductOverrides = async (clientId, productId) => {
  const response = await httpClient.get(`/api/clients/${clientId}/products/${productId}/overrides`);
  const data = response.data;
  
  if (data.overrides) {
    data.overrides = parseClientData(data.overrides);
  }
  
  return data;
};

// Create or update client product overrides
const updateClientProductOverrides = async (clientId, productId, overrideData) => {
  const response = await httpClient.put(`/api/clients/${clientId}/products/${productId}/overrides`, overrideData);
  const data = response.data;
  
  if (data.overrides) {
    data.overrides = parseClientData(data.overrides);
  }
  
  return data;
};

// Delete client product overrides
const deleteClientProductOverrides = async (clientId, productId) => {
  const response = await httpClient.delete(`/api/clients/${clientId}/products/${productId}/overrides`);
  return response.data;
};

// Search clients
const searchClients = async (query, page = 1, pageSize = 20) => {
  const response = await httpClient.post('/api/clients/search', {
    query,
    page,
    page_size: pageSize
  });
  const data = response.data;
  
  if (data.clients) {
    data.clients = parseClientArray(data.clients);
  }
  
  return data;
};

// Batch operations
const batchUpdateClientInventory = async (clientId, updates) => {
  const response = await httpClient.put(`/api/clients/${clientId}/inventory/batch-update`, { updates });
  const data = response.data;
  
  if (data.updated_products) {
    data.updated_products = parseClientArray(data.updated_products);
  }
  
  return data;
};

// Get inventory audit history for a product
const getInventoryAuditHistory = async (productId, page = 1, pageSize = 20) => {
  const params = {
    page,
    page_size: pageSize
  };

  const response = await httpClient.get(`/api/inventory/audit-history/${productId}`, { params });
  const data = response.data;
  
  if (data.audit_history) {
    data.audit_records = JSON.parse(data.audit_history);
  }
  
  return data;
};

const clientService = {
  // Client management
  getClients,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  searchClients,

  // Client inventory management
  getClientInventory,
  getClientInventoryEnhanced,
  updateClientInventory,
  batchUpdateClientInventory,
  
  // Client product management
  getClientProducts,
  getClientProductOverrides,
  updateClientProductOverrides,
  deleteClientProductOverrides,
  
  // Audit and history
  getInventoryAuditHistory,
};

export default clientService;
